import React, { useEffect, useState } from 'react';
import {
    Box, Flex, Heading, Text, Badge, Button, Grid, GridItem,
    Spinner, Alert, AlertIcon, AlertDescription, useColorModeValue,
    useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
    Tooltip, HStack, Tag, TagLabel, TagLeftIcon, Progress,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd, MdCheckCircle, MdCancel, MdLink, MdOpenInNew,
    MdRefresh, MdApi,
} from 'react-icons/md';
import {
    SiWoocommerce, SiShopify, SiGooglesheets,
} from 'react-icons/si';
import axiosClient from '../../../axios-client';
import { useStateContext } from '../../../context/ContextProvider';

// ─── icon map per API code ────────────────────────────────────────────────────
const API_ICONS = {
    woocommerce: <SiWoocommerce size={36} color="#96588A" />,
    shopify: <SiShopify size={36} color="#96BF48" />,
    google_sheets: <SiGooglesheets size={36} color="#34A853" />,
    lightfunnels: <MdApi size={36} color="#FF6B35" />,
    storeep: <MdApi size={36} color="#2D9CDB" />,
    youcan: <MdApi size={36} color="#E91E63" />,
};

const API_COLORS = {
    woocommerce: 'purple',
    shopify: 'green',
    google_sheets: 'teal',
    lightfunnels: 'orange',
    storeep: 'blue',
    youcan: 'pink',
};

export default function MyAPIs() {
    const { t } = useTranslation();
    const { user, dataSubscriptionUser, getAbonnementClient, currentBusiness } = useStateContext();
    const toast = useToast();
    const navigate = useNavigate();

    const [apiTypes, setApiTypes] = useState([]);
    const [connections, setConnections] = useState([]);        // business-scoped (for display)
    const [allConnections, setAllConnections] = useState([]);  // cross-business (for counting)
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(null);
    const [disconnecting, setDisconnecting] = useState(null);

    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [pendingApiType, setPendingApiType] = useState(null);

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const subText = useColorModeValue('gray.500', 'gray.400');
    const connectedBg = useColorModeValue('green.50', 'green.900');
    const hubBg = useColorModeValue('gray.50', 'gray.800');

    // Subscription limits
    // IMPORTANT: 0 = feature BLOCKED on this plan (not unlimited).
    //            N > 0 = at most N active connections allowed.
    const maxPlatforms = dataSubscriptionUser?.max_store_platforms ?? 0;
    const hasSubscription = !!dataSubscriptionUser?.has_subscription;
    const platformsBlocked = hasSubscription && maxPlatforms === 0;
    // Count active connections across ALL businesses (subscription-level limit)
    const activePlatforms = allConnections.filter(c => c.is_active).length;
    const isLimitReached = maxPlatforms > 0 && activePlatforms >= maxPlatforms;

    // ── Fetch catalogue + connections ─────────────────────────────────────
    const fetchAll = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const [typesRes, connsRes, allConnsRes] = await Promise.all([
                axiosClient.get('/api-types/'),
                // Business-scoped: used only for listing cards in the current business
                axiosClient.get(`/api-connections/?user_id=${user.id}&business=${currentBusiness?.id}`),
                // Cross-business: used for the subscription limit count
                axiosClient.get(`/api-connections/?user_id=${user.id}`),
            ]);
            setApiTypes(typesRes.data?.results || typesRes.data || []);
            setConnections(connsRes.data?.results || connsRes.data || []);
            setAllConnections(allConnsRes.data?.results || allConnsRes.data || []);
        } catch (err) {
            // // console.error(err);
            toast({
                description: t('Erreur lors du chargement des APIs.'),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, [user?.id]);

    // ── Get connection for a given api_type id ────────────────────────────
    const getConnection = (apiTypeId) =>
        connections.find((c) => c.api_type === apiTypeId) || null;

    // ── Connect ───────────────────────────────────────────────────────────
    const handleConnect = async (apiType) => {
        if (platformsBlocked) {
            toast({
                title: t('Fonctionnalit\u00e9 non disponible'),
                description: t('Votre plan actuel ne permet pas les connexions aux plateformes e-commerce. Passez \u00e0 un plan sup\u00e9rieur.'),
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right',
            });
            return;
        }
        if (isLimitReached) {
            toast({
                title: t('Limite atteinte'),
                description: t(`Vous avez atteint le maximum de connexions autoris\u00e9es (${maxPlatforms}) par votre abonnement.`),
                status: 'warning',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right',
            });
            return;
        }
        setConnecting(apiType.id);
        try {
            const res = await axiosClient.post('/api-connections/', {
                user_id: user.id,
                api_type: apiType.id,
                business: currentBusiness.id,
            });
            toast({
                description: t(`Connexion à ${apiType.name} créée avec succès !`),
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
            navigate('/api-detail', { state: { connection: res.data, apiType } });
            getAbonnementClient();
        } catch (err) {
            // console.log("error : ", err);

            const msg = err?.response?.data?.error || t('Erreur lors de la connexion.');
            toast({
                description: msg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setConnecting(null);
    };

    // ── Disconnect ────────────────────────────────────────────────────────
    const handleDisconnect = async () => {
        if (!pendingApiType) return;
        const conn = getConnection(pendingApiType.id);
        if (!conn) return;
        setDisconnecting(conn.id);
        try {
            await axiosClient.delete(`/api-connections/${conn.id}/?user_id=${user.id}`);
            toast({
                description: t(`Connexion à ${pendingApiType.name} supprimée.`),
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
            fetchAll();
            getAbonnementClient();
        } catch (err) {
            toast({
                description: t('Erreur lors de la déconnexion.'),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setDisconnecting(null);
        onConfirmClose();
        setPendingApiType(null);
    };

    // ── Toggle active ─────────────────────────────────────────────────────
    const handleToggle = async (conn) => {
        const wouldActivate = !conn.is_active;
        if (wouldActivate && isLimitReached) {
            toast({
                title: t('Limite atteinte'),
                description: t(`Vous avez atteint le maximum de connexions actives (${maxPlatforms}) autorisées par votre abonnement. Désactivez-en une autre d'abord.`),
                status: 'warning',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right',
            });
            return;
        }
        try {
            await axiosClient.patch(`/api-connections/${conn.id}/toggle_active/?user_id=${user.id}`);
            fetchAll();
            getAbonnementClient();
        } catch (err) {
            toast({
                description: err?.response?.data?.error || t('Erreur.'),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="300px">
                <Spinner size="xl" color="teal.400" />
            </Flex>
        );
    }

    return (
        <Box bg={hubBg} minH="100vh" p={{ base: 4, md: 8 }}>
            {/* ── Header ───────────────────────────────────────────────── */}
            <Flex
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                mb={8}
                gap={4}
            >
                <Box>
                    <Heading size="lg" mb={1}>
                        🔗 {t('Mes Sources de Commandes')}
                    </Heading>
                    <Text color={subText} fontSize="sm">
                        {t('Connectez vos plateformes e-commerce pour importer les commandes automatiquement.')}
                    </Text>
                </Box>
                <HStack>
                    <Tag colorScheme="teal" size="lg" rounded="full" px={4}>
                        <TagLeftIcon as={MdCheckCircle} />
                        <TagLabel>{activePlatforms} {t('connectée(s)')}</TagLabel>
                    </Tag>
                    <Button size="sm" leftIcon={<MdRefresh />} onClick={fetchAll} variant="outline">
                        {t('Actualiser')}
                    </Button>
                </HStack>
            </Flex>

            {/* ── Subscription limit bar ────────────────────────────────── */}
            {platformsBlocked && (
                <Alert status="error" mb={6} rounded="xl" p={5}>
                    <AlertIcon boxSize={6} />
                    <Box ml={2}>
                        <Text fontWeight="bold" mb={1}>
                            {t('Fonctionnalit\u00e9 non disponible sur votre plan actuel')}
                        </Text>
                        <AlertDescription fontSize="sm">
                            {t('Votre plan ne permet pas les connexions aux plateformes e-commerce. Passez \u00e0 un plan sup\u00e9rieur.')}
                        </AlertDescription>
                    </Box>
                </Alert>
            )}

            {!platformsBlocked && maxPlatforms > 0 && (
                <Box mb={6} p={4} bg={cardBg} rounded="xl" shadow="sm" borderWidth="1px" borderColor={borderC}>
                    <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm" fontWeight="semibold">{t('Plateformes e-commerce actives')}</Text>
                        <Text fontSize="sm" color={isLimitReached ? 'red.500' : subText} fontWeight="bold">
                            {activePlatforms} / {maxPlatforms}
                        </Text>
                    </Flex>
                    <Progress
                        value={(activePlatforms / maxPlatforms) * 100}
                        colorScheme={isLimitReached ? 'red' : activePlatforms / maxPlatforms >= 0.8 ? 'orange' : 'teal'}
                        rounded="full"
                        size="sm"
                    />
                    {isLimitReached && (
                        <Alert status="warning" mt={3} rounded="lg" py={2}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                {t(`Limite atteinte. Désactivez une connexion existante pour en activer une nouvelle.`)}
                            </AlertDescription>
                        </Alert>
                    )}
                </Box>
            )}

            {/* ── Inactive connections warning ──────────────────────────── */}
            {connections.some((c) => !c.is_active) && (
                <Alert status="info" mb={6} rounded="lg">
                    <AlertIcon />
                    <AlertDescription>
                        {t('Certaines connexions sont désactivées. Vérifiez votre abonnement ou réactivez-les manuellement.')}
                    </AlertDescription>
                </Alert>
            )}

            {/* ── API Cards Grid ────────────────────────────────────────── */}
            <Grid
                templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                gap={6}
            >
                {apiTypes.map((apiType) => {
                    const conn = getConnection(apiType.id);
                    const isConnected = !!conn;
                    const isActive = conn?.is_active;
                    const colorScheme = API_COLORS[apiType.code] || 'gray';
                    // Card blocked when: plan forbids e-store (0) OR limit reached for inactive/new cards
                    const wouldExceedLimit = platformsBlocked || (!isActive && isLimitReached);

                    return (
                        <GridItem key={apiType.id}>
                            <Box
                                bg={isConnected ? connectedBg : cardBg}
                                border="2px solid"
                                borderColor={isConnected ? `${colorScheme}.300` : borderC}
                                rounded="2xl"
                                p={6}
                                shadow={isConnected ? 'md' : 'sm'}
                                transition="all 0.2s"
                                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                                position="relative"
                                opacity={wouldExceedLimit ? 0.85 : 1}
                            >
                                {/* Connected badge */}
                                {isConnected && (
                                    <Badge
                                        position="absolute"
                                        top={3}
                                        right={3}
                                        colorScheme={isActive ? 'green' : 'red'}
                                        rounded="full"
                                        px={2}
                                    >
                                        {isActive ? t('Actif') : t('Inactif')}
                                    </Badge>
                                )}

                                {/* Icon + Name */}
                                <Flex align="center" gap={3} mb={4}>
                                    <Box>
                                        {API_ICONS[apiType.code] || <MdApi size={36} />}
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold" fontSize="lg">
                                            {apiType.name}
                                        </Text>
                                        <Badge colorScheme={colorScheme} fontSize="xs">
                                            {apiType.code}
                                        </Badge>
                                    </Box>
                                </Flex>

                                {/* Description */}
                                <Text fontSize="sm" color={subText} mb={5} noOfLines={3}>
                                    {apiType.description || t('Intégration e-commerce.')}
                                </Text>

                                {/* Stats row (if connected) */}
                                {isConnected && (
                                    <Flex gap={4} mb={4} fontSize="xs" color={subText}>
                                        <Box>
                                            <Text fontWeight="semibold">{conn.orders_imported}</Text>
                                            <Text>{t('commandes')}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="semibold">
                                                {conn.last_used
                                                    ? new Date(conn.last_used).toLocaleDateString()
                                                    : '-'}
                                            </Text>
                                            <Text>{t('dernière utilisation')}</Text>
                                        </Box>
                                    </Flex>
                                )}

                                {/* Actions */}
                                <Flex gap={2} flexWrap="wrap">
                                    {!isConnected ? (
                                        <Tooltip
                                            label={
                                                platformsBlocked
                                                    ? t('Votre plan ne permet pas les connexions e-commerce')
                                                    : isLimitReached
                                                        ? t(`Limite de ${maxPlatforms} plateformes atteinte`)
                                                        : ''
                                            }
                                            isDisabled={!platformsBlocked && !isLimitReached}
                                        >
                                            <Button
                                                size="sm"
                                                colorScheme={colorScheme}
                                                leftIcon={<MdAdd />}
                                                isLoading={connecting === apiType.id}
                                                onClick={() => handleConnect(apiType)}
                                                w="full"
                                                isDisabled={platformsBlocked || isLimitReached}
                                                opacity={platformsBlocked || isLimitReached ? 0.5 : 1}
                                            >
                                                {t('Connecter')}
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <>
                                            <Button
                                                size="sm"
                                                colorScheme={colorScheme}
                                                leftIcon={<MdOpenInNew />}
                                                onClick={() =>
                                                    navigate('/api-detail', {
                                                        state: { connection: conn, apiType },
                                                    })
                                                }
                                                flex={1}
                                            >
                                                {t('Voir détails')}
                                            </Button>
                                            <Tooltip label={
                                                platformsBlocked
                                                    ? t('Fonctionnalit\u00e9 non disponible sur ce plan')
                                                    : wouldExceedLimit
                                                        ? t(`Limite de ${maxPlatforms} connexions actives atteinte`)
                                                        : isActive ? t('D\u00e9sactiver') : t('Activer')
                                            }>
                                                <Button
                                                    size="sm"
                                                    colorScheme={isActive ? "yellow" : "green"}
                                                    leftIcon={isActive ? <MdCancel /> : <MdCheckCircle />}
                                                    onClick={() => handleToggle(conn)}
                                                    isDisabled={wouldExceedLimit}
                                                >
                                                    {isActive ? "Désactiver" : "Activer"}
                                                </Button>
                                            </Tooltip>
                                            <Tooltip label={t('Déconnecter')}>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    isLoading={disconnecting === conn.id}
                                                    onClick={() => {
                                                        setPendingApiType(apiType);
                                                        onConfirmOpen();
                                                    }}
                                                >
                                                    <MdLink />
                                                </Button>
                                            </Tooltip>
                                        </>
                                    )}
                                </Flex>
                            </Box>
                        </GridItem>
                    );
                })}
            </Grid>

            {/* ── Disconnect confirmation ────────────────────────────────── */}
            <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('Confirmer la déconnexion')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            {t('Êtes-vous sûr de vouloir supprimer la connexion à')}{' '}
                            <strong>{pendingApiType?.name}</strong> ?{' '}
                            {t('Toutes les configurations et le token associé seront perdus.')}
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button
                            colorScheme="red"
                            isLoading={!!disconnecting}
                            onClick={handleDisconnect}
                        >
                            {t('Supprimer')}
                        </Button>
                        <Button variant="ghost" onClick={onConfirmClose}>
                            {t('Annuler')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
